import {
  PageHeader,
} from "@/util-components/page-layout/page-layout";
import PassedGames from "./passed-games";
import SponsorKPIs from "./sponsor-kpis";
import UpcomingBirthdays from "./upcoming-birthdays";
import UpcomingGames from "./upcoming-games";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { Calendar, PartyPopper, Plus, Trophy, ChevronRight } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { Context } from "@/util-components/root/context";
import CircularText from "@/ui-components/ui/shadcn-io/circular-text";
import { Button } from "@/ui-components/ui/button";
import { Link } from "react-router-dom";

/**
 * CustomerHome Component
 *
 * A modern, minimalist dashboard heavily inspired by clean fintech UIs (e.g. TradeRepublic).
 * Focuses on content, whitespace, and gentle interactions.
 * 
 * Features:
 * - Dynamic greeting based on time of day.
 * - Circular text animation.
 * - Dashboard widgets for upcoming games, birthdays, and stats.
 * - Welcome dialog for new subscribers.
 */
const CustomerHome = () => {
  const { user } = useContext(Context);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const hours = currentTime.getHours();

  // Dynamic Greeting
  let greeting = "Guten Morgen";
  if (hours >= 12 && hours < 18) greeting = "Guten Tag";
  if (hours >= 18) greeting = "Guten Abend";

  // Date Formatting
  const today = currentTime;
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };
  const dateString = today.toLocaleDateString("de-DE", dateOptions);

  // Circular Text Formatting
  const circularTextString = " NEUER POST • NEUER POST •".toUpperCase();

  return (
    <div className="h-full bg-gray-50 overflow-x-hidden">
      <PageHeader className="px-4 relative overflow-visible bg-transparent border-b-0">
        <div className="flex items-center justify-between gap-6 w-full py-2 z-10">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-400 uppercase tracking-widest">
              <span>{dateString}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mt-1">
              {greeting}, {user?.firstName} 👋
            </h1>
          </div>

          {/* Spinning CTA - Top Right */}
          <div className="hidden sm:flex shrink-0 self-center flex items-center justify-end z-10">
            <div className="flex items-center absolute z-10 transform translate-x-[50px]">
              <div className="animate-[spin_22s_linear_infinite_reverse]">
                <CircularText
                  text={circularTextString}
                  spinDuration={22}
                  className="w-[150px] h-[150px] font-bold text-xl tracking-widest pointer-events-none select-none text-blue-600/40"
                  direction="counterclockwise"
                />
              </div>
              <Link
                to="/post-generator/templates"
                aria-label="Neuen Post erstellen"
              >
                <Button
                  size="icon"
                  aria-label="Neuen Post erstellen"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full w-12 h-12 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-300"
                >
                  <Plus className="w-6 h-6 text-white" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="space-y-12 pb-12 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
        {/* Top Section: Quick Actions / Critical Info could go here */}

        <div className="space-y-6">
          <SectionHeader
            icon={Calendar}
            title="Das steht an"
            description="Deine nächsten Spiele und Events im Überblick."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <DashboardWidget title="Nächste Spiele" href="/spiele">
              <UpcomingGames />
            </DashboardWidget>

            <DashboardWidget title="Geburtstage" href="/spieler">
              <UpcomingBirthdays />
            </DashboardWidget>
          </div>
        </div>

        <div className="space-y-6">
          <SectionHeader
            icon={Trophy}
            title="Rückblick"
            description="Ergebnisse und Performance der letzten Spiele."
          />
          <DashboardWidget>
            <PassedGames />
          </DashboardWidget>
        </div>

        <div className="space-y-6">
          <SectionHeader
            icon={PartyPopper}
            title="Sponsoren"
            description="Deine aktuellen Sponsoring-Kennzahlen."
          />
          <DashboardWidget>
            <SponsorKPIs />
          </DashboardWidget>
        </div>
      </div>
    </div>
  );
};

function DashboardWidget({
  children,
  title,
  href,
}: {
  children: React.ReactNode;
  title?: string;
  href?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full transition-all hover:shadow-md hover:border-gray-200">
      {title && (
        <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
          {href ? (
            <Link to={href} className="flex items-center justify-between group">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </Link>
          ) : (
            <h3 className="font-semibold text-gray-900">{title}</h3>
          )}
        </div>
      )}
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default CustomerHome;
